import { IStringIdEntity } from './base.interface';
import { ICar } from './car.interface';
import { IConnector, IConnector_FE_SessionHistory } from './connector.interface';
import { ErrorCodeValues } from './enums/errorCode.enum';
import { SessionClosureValues } from './enums/sessionClosure.enum';
import { IUser, IUser_FE_SessionHistory } from './user.interface';
import { AuthTypeValues } from './enums/authType.enum';
import { CPOsValues, EMSPsValues } from './enums/cpo.enum';
import { StateValues } from './enums/state.enum';
import { SessionStateValues } from './enums/evseState.enum';
import { SessionCmdValues, ValueOf } from './enums/sessionCmd.enum';
import { ITariff } from './ocpi/tariff.interface';
import { IChargeDetailRecord, IChargeDetailRecord_FE_SessionHistory } from './ocpi/cdr.interface';
import { Point } from 'geojson';
import { SessionCdrStateValues } from './enums/sessionCdrState.enum';
import { ISessionIntegritySummary } from './session-integrity.interface';

export interface ISession extends IStringIdEntity {
  userId: string;
  user?: IUser;
  carId: number;
  car?: ICar;
  connectorId: number;
  connector?: IConnector;
  innerState?: string;
  /**
   * Stato della sessione. È QUESTA la colonna: il vocabolario stringa (`SessionState`) è una
   * vista sul numerico, derivata con `sessionStateOf(state, endedAt)` — non esiste una colonna
   * separata da tenere sincronizzata.
   */
  state: StateValues;
  reservedAt?: Date;
  bookingEndAt?: Date;
  startedAt?: Date;
  fullyChargedAt?: Date;
  cancelledAt?: Date;
  stoppedAt?: Date;
  endedAt?: Date;
  /** Scadenza dell'attesa pre-auth (State.PENDING_AUTH): oltre, lo sweep aborta la sessione e fa void dell'hold. */
  preauthDeadlineAt?: Date | null;
  energy?: number;
  power?: number;
  powerLimit?: number;
  soc?: number;
  transactionId?: number;
  deposit?: number; // Deposit paid on start transaction by the user 1€ flat
  balance?: number; // Balance to be paid by the user
  energyPrice?: number;
  reservationPrice?: number;
  tariffId?: string;
  tariff?: ITariff;
  occupationPrice?: number;
  energyCost?: number;
  occupationCost?: number;
  reservationCost?: number;
  totalCost?: number;
  metadata: any;
  cpo?: CPOsValues;
  operatorId?: string;
  cpoPartnerSessionId?: string;
  emspPartnerSessionId?: string;
  sponsorshipPolicyId?: number;
  sponsorshipPricing?: any;
  providerId?: string;
  providerSessionId?: string;
  tariffJson?: any;
  authType?: AuthTypeValues,
  cdr?: IChargeDetailRecord,
  cdrState?: SessionCdrStateValues;
  occupationTime?: number,
  /**
   * Chiusura ANOMALA: la sessione è finita operativamente ma i dati non sono definitivi.
   * NULL/assente = chiusura normale, consolidamento immediato (il comportamento di sempre).
   * Vedi `SessionClosure` in `enums/sessionClosure.enum.ts`.
   */
  closureState?: SessionClosureValues;
  /** Confine della finestra FATTURABILE quando è precedente a `endedAt` (vedi `billableEnd`). */
  billableUntil?: Date;
  /** Scadenza della finestra di riconciliazione delle sessioni anomale. */
  reconciliationDeadlineAt?: Date;
  /** Diario della chiusura anomala: causa, dati usati, esito. Per il backoffice. */
  closureDetails?: any;
  ocpiSessionId?: string;
  emsp?: EMSPsValues;
  idTag?: string; // rfid tag or session id tag
  endReason?: SessionEndReasonValues;
  problematic?: boolean;
  problemDetails?: any; 
  _integrityErrorsCount?: number;
  _integrityWarningsCount?: number;
  integrity?: ISessionIntegritySummary;
  paid?: number;
}

export interface ISession_FE_SessionHistory extends IStringIdEntity {
  user?: IUser_FE_SessionHistory;
  car?: ICar;
  connector?: IConnector_FE_SessionHistory;
  reservedAt?: Date;
  bookingEndAt?: Date;
  startedAt?: Date;
  fullyChargedAt?: Date;
  cancelledAt?: Date;
  stoppedAt?: Date;
  endedAt?: Date;
  energy?: number;
  occupationTime?: number;
  /** Chiusura anomala: serve all'app/storico per mostrare "in verifica" invece di un totale finto. */
  closureState?: SessionClosureValues;
  cpo?: CPOsValues;
  tariffJson?: any;
  cdr?: IChargeDetailRecord_FE_SessionHistory
}

export interface ISessionCommand {
  carId?: number;
  sessionId?: string;
  transactionId?: number;
  connectorId: number;
  cmd: SessionCmdValues;
  powerLimit?: number;
  params?: any;
  authType?: AuthTypeValues;
  createdBy?: string;
  updatedBy?: string;
  clientId?: string;
  coord?: Point;
  emsp?: EMSPsValues;
  providerId?: string;
}

export interface ISessionResult {
  errorCode: ErrorCodeValues;
  session?: ISession;
  connector?: IConnector
  message?: string;
}

export interface ISessionUserHistoryMonth {
  prevMonth: string;
  month: string;
  isLastMonth: boolean;
  sessions: ISession_FE_SessionHistory[];
}


// export enum SessionEndReason {
//   OCPI_CPO_COMMAND_RESULT_FAILED,
//   UNKNOWN,
// }

export const SessionEndReason = Object.freeze({
  OCPI_CPO_COMMAND_RESULT_FAILED: "OCPI_CPO_COMMAND_RESULT_FAILED",
  REMOTE_STOP: "REMOTE_STOP",                 
  LOCAL_STOP: "LOCAL_STOP",                   
  TIMEOUT: "TIMEOUT",          
  STARTING_TIMEOUT_TRIGGERED: "STARTING_TIMEOUT_TRIGGERED",              
  USER_DISCONNECTED_CABLE: "USER_DISCONNECTED_CABLE", 
  SESSION_CANCELLED: "SESSION_CANCELLED",    
  NEVER_STARTED: "NEVER_STARTED",             
  UNAUTHORIZED_SESSION: "UNAUTHORIZED_SESSION",
  PASSTHROUGH_CONNECTOR_AVAILABLE: "PASSTHROUGH_CONNECTOR_AVAILABLE",
  CONNECTOR_FAULT: "CONNECTOR_FAULT",
  UNKNOWN: "UNKNOWN",
  TROUBLESHOOTING_ADMIN_FORCED: "TROUBLESHOOTING_ADMIN_FORCED",
  CONNECTOR_STATE_ERROR: "CONNECTOR_STATE_ERROR",
  RESERVATION_CANCELLED_NO_DEPOSIT_ERROR: "RESERVATION_CANCELLED_NO_DEPOSIT_ERROR",
  RESERVATION_CANCELLED_NO_DEPOSIT: "RESERVATION_CANCELLED_NO_DEPOSIT",
  TIMEOUT_CONNECTOR_WAS_AVAILABLE: "TIMEOUT_CONNECTOR_WAS_AVAILABLE",
  STUCK_SESSION_WITHOUT_ACTIVITY: "STUCK_SESSION_WITHOUT_ACTIVITY",
  PREAUTH_FAILED: "PREAUTH_FAILED",   // hold pre-auth non autorizzato → ricarica mai avviata
  // Sessione roaming che il CPO terzo non ha mai chiuso, chiusa da noi DOPO che il CPO stesso ha
  // confermato — su richiesta esplicita, non a tempo — che l'EVSE è libero / la sessione è finita.
  // Non è un timeout: se il CPO tace non si chiude niente (vedi RoamingStuckSessionVerifierService).
  ROAMING_CPO_CONFIRMED_EVSE_FREE: "ROAMING_CPO_CONFIRMED_EVSE_FREE"
});
export type SessionEndReasonValues = ValueOf<typeof SessionEndReason>;
export type SessionEndReasonKeys = keyof typeof SessionEndReason;

export interface IEndSession {
  sessionId: string;
  connectorId: number;
  endedAt: Date;
  reason?: SessionEndReasonValues;
  removeOcpiSessionId?: boolean;
}
